import React, { type ComponentType, type LazyExoticComponent } from 'react';

const RELOAD_GUARD_KEY = 'chunk-reload-attempted';
const RETRY_DELAY_MS = 500;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Resolve a dynamic import, surviving stale-chunk failures after a deploy.
 *
 * Hashed chunk filenames are purged on redeploy, so an old client (e.g. a link
 * opened from an email days later) can request a chunk that 404s. We retry once
 * for transient blips, then force a single full reload to fetch `index.html`
 * with the current chunk hashes. A `sessionStorage` guard prevents reload loops
 * on a genuinely broken deploy — there we re-throw so the error boundary shows
 * its recovery UI instead.
 */
export async function loadWithReload<TModule>(
  loader: () => Promise<TModule>
): Promise<TModule> {
  try {
    const mod = await loader();
    // Success — allow a future deploy to reload again.
    sessionStorage.removeItem(RELOAD_GUARD_KEY);
    return mod;
  } catch {
    // One quick retry absorbs transient network failures.
    try {
      await delay(RETRY_DELAY_MS);
      const mod = await loader();
      sessionStorage.removeItem(RELOAD_GUARD_KEY);
      return mod;
    } catch (error) {
      const alreadyReloaded = sessionStorage.getItem(RELOAD_GUARD_KEY);
      if (!alreadyReloaded) {
        // Likely a stale chunk — reload once to pick up fresh hashes.
        sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
        window.location.reload();
        // Block the rejection from surfacing while the reload is in flight.
        await new Promise<never>(() => {});
      }
      // We already reloaded and it still failed: re-throw for the boundary.
      throw error;
    }
  }
}

/** Lazy-load a module's named export as the default for `React.lazy`. */
export function lazyNamed<
  TModule extends Record<string, ComponentType<object>>,
>(
  loader: () => Promise<TModule>,
  exportName: keyof TModule & string
): LazyExoticComponent<ComponentType<object>> {
  return React.lazy(async () => {
    const mod = await loadWithReload(loader);
    return { default: mod[exportName]! };
  });
}
